import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { subscribeToSubscriptions } from '@/components/subscriptions/services/subscriptions-api';
import { Subscription } from '@/components/subscriptions/types/subscription';
import { UserRole } from '@/components/authnz/types/rbac';
import logger from '@/lib/logging';

export interface UserInfo {
  roles: UserRole[];
  activeSubscription: Subscription | null;
  loading: boolean;
}

const UserInfoContext = createContext<UserInfo | undefined>(undefined);

function findActiveSubscription(
  subscriptions: Subscription[],
  user: { uid: string; email?: string | null }
): Subscription | null {
  const active = subscriptions.find((sub) => {
    const isActiveStatus = sub.status === 'active' || sub.status === 'trialing';
    const isUserEmail = user.email && sub.userEmails.includes(user.email);
    const isUserId = sub.userId === user.uid;
    return isActiveStatus && (isUserEmail || isUserId);
  });
  return active ?? null;
}

/**
 * UserInfoProvider
 *
 * Central context to manage user roles and subscription state.
 * Subscriptions are polled via GET /subscriptions (single instance app-wide).
 */
export function UserInfoProvider({ children }: { children: ReactNode }) {
  const { user, serverUser } = useAuth();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [activeSubscription, setActiveSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !serverUser) {
      setRoles([]);
      setActiveSubscription(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    let isMounted = true;

    if (serverUser?.roles) {
      setRoles(serverUser.roles as UserRole[]);
    } else {
      setRoles([]);
    }

    logger.debug('[UserInfoContext] Polling subscriptions via HTTP');

    const unsubscribe = subscribeToSubscriptions(
      (subscriptions) => {
        if (!isMounted) return;
        setActiveSubscription(findActiveSubscription(subscriptions, user));
        setLoading(false);
      },
      60_000,
      (error) => {
        logger.error('[UserInfoContext] Error loading subscriptions:', error);
        if (isMounted) {
          setActiveSubscription(null);
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
      logger.debug('[UserInfoContext] Stopping subscription poll');
      unsubscribe();
    };
  }, [user, serverUser]);

  return (
    <UserInfoContext.Provider value={{ roles, activeSubscription, loading }}>
      {children}
    </UserInfoContext.Provider>
  );
}

/**
 * Access the centralized user info (roles and subscription).
 */
export function useUserInfoContext() {
  const context = useContext(UserInfoContext);
  if (context === undefined) {
    throw new Error('useUserInfoContext must be used within a UserInfoProvider');
  }
  return context;
}
